// src/components/admin/UserFriendlySupportEcosystemEditor.tsx
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Tabs, Tab, Button, Card, CardContent, IconButton,
  TextField, useTheme, alpha, Grid, CircularProgress, Accordion, AccordionSummary, AccordionDetails,
  Stack
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon,
  Business as BusinessIcon, People as PeopleIcon,
  MonetizationOn as MoneyIcon, Science as ScienceIcon,
  ExpandMore as ExpandMoreIcon, Save as SaveIcon
} from '@mui/icons-material';

interface SupportEcosystemItem {
  id?: string;
  category: 'places_infrastructure' | 'events_networks' | 'funding_incentives' | 'talent_innovation';
  name: string;
  leadText: string;
  bulletPoints: string[];
  intro: string;
  details: any;
  isActive: boolean;
  displayOrder: number;
}

interface UserFriendlySupportEcosystemEditorProps {
  industryId: string;
  subIndustryId?: string;
  onUpdate?: () => void;
}

const UserFriendlySupportEcosystemEditor: React.FC<UserFriendlySupportEcosystemEditorProps> = ({
  industryId,
  subIndustryId,
  onUpdate
}) => {
  const theme = useTheme();
  const [tabValue, setTabValue] = useState(0);
  const [items, setItems] = useState<SupportEcosystemItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [expandedAccordion, setExpandedAccordion] = useState<string | false>('basic');

  const [formData, setFormData] = useState<SupportEcosystemItem>({
    category: 'places_infrastructure',
    name: '',
    leadText: '',
    bulletPoints: [],
    intro: '',
    details: {},
    isActive: true,
    displayOrder: 0
  });

  const categories = [
    { value: 'places_infrastructure', label: 'Places & Infrastructure', icon: <BusinessIcon />, color: theme.palette.warning },
    { value: 'events_networks', label: 'Events & Networks', icon: <PeopleIcon />, color: theme.palette.info },
    { value: 'funding_incentives', label: 'Funding & Initiatives', icon: <MoneyIcon />, color: theme.palette.success },
    { value: 'talent_innovation', label: 'Talent & Innovation', icon: <ScienceIcon />, color: theme.palette.secondary }
  ];

  useEffect(() => {
    loadItems();
  }, [industryId, subIndustryId]);

  // Load form data when tab changes or items load
  useEffect(() => {
    const currentCategory = categories[tabValue].value;
    const existingItem = items.find(item => item.category === currentCategory);

    if (existingItem) {
      setFormData(existingItem);
    } else {
      setFormData({
        category: currentCategory as any,
        name: '',
        leadText: '',
        bulletPoints: [],
        intro: '',
        details: getEmptyDetailsForCategory(currentCategory),
        isActive: true,
        displayOrder: 0
      });
    }
    setExpandedAccordion('basic');
  }, [tabValue, items]);

  const loadItems = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const url = subIndustryId
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/support-ecosystem/sub-industry/${subIndustryId}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/support-ecosystem/industry/${industryId}`;

      const response = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setItems(Array.isArray(data) ? data : []);
      }
    } catch (error) {
      console.error('Error loading support ecosystem items:', error);
    } finally {
      setLoading(false);
    }
  };

  const getEmptyDetailsForCategory = (category: string) => {
    switch (category) {
      case 'places_infrastructure':
        return { keyLocations: [], supportAssets: [] };
      case 'events_networks':
        return { topEvents: [], associations: [] };
      case 'funding_incentives':
        return { fundingPrograms: [], incentives: [], investorTools: [] };
      case 'talent_innovation':
        return { universities: [], innovationHubs: [], skillsLocalization: [] };
      default:
        return {};
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const url = formData.id
        ? `${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/support-ecosystem/${formData.id}`
        : `${process.env.REACT_APP_API_URL || 'http://localhost:4001'}/api/support-ecosystem`;

      const payload = {
        ...formData,
        industryId,
        subIndustryId: subIndustryId || null
      };

      const response = await fetch(url, {
        method: formData.id ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        await loadItems();
        if (onUpdate) onUpdate();
      }
    } catch (error) {
      console.error('Error saving support ecosystem item:', error);
    } finally {
      setSaving(false);
    }
  };

  // Helper functions for managing arrays in details
  const addTableRow = (section: string, emptyRow: any) => {
    const details = { ...formData.details };
    if (!details[section]) details[section] = [];
    details[section].push(emptyRow);
    setFormData({ ...formData, details });
  };

  const updateTableRow = (section: string, index: number, field: string, value: any) => {
    const details = { ...formData.details };
    details[section][index][field] = value;
    setFormData({ ...formData, details });
  };

  const deleteTableRow = (section: string, index: number) => {
    const details = { ...formData.details };
    details[section].splice(index, 1);
    setFormData({ ...formData, details });
  };

  const addCardItem = (section: string) => {
    const details = { ...formData.details };
    if (!details[section]) details[section] = [];
    details[section].push({ name: '', bulletPoints: [''] });
    setFormData({ ...formData, details });
  };

  const updateCardName = (section: string, index: number, name: string) => {
    const details = { ...formData.details };
    details[section][index].name = name;
    setFormData({ ...formData, details });
  };

  const addBulletPoint = (section: string, cardIndex: number) => {
    const details = { ...formData.details };
    details[section][cardIndex].bulletPoints.push('');
    setFormData({ ...formData, details });
  };

  const updateBulletPoint = (section: string, cardIndex: number, bulletIndex: number, value: string) => {
    const details = { ...formData.details };
    details[section][cardIndex].bulletPoints[bulletIndex] = value;
    setFormData({ ...formData, details });
  };

  const deleteBulletPoint = (section: string, cardIndex: number, bulletIndex: number) => {
    const details = { ...formData.details };
    details[section][cardIndex].bulletPoints.splice(bulletIndex, 1);
    setFormData({ ...formData, details });
  };

  const deleteCardItem = (section: string, index: number) => {
    const details = { ...formData.details };
    details[section].splice(index, 1);
    setFormData({ ...formData, details });
  };

  // Render form sections based on category
  const renderCategoryForm = () => {
    const details = formData.details || {};

    switch (formData.category) {
      case 'places_infrastructure':
        return (
          <>
            {/* Key Locations Table */}
            <Accordion expanded={expandedAccordion === 'keyLocations'} onChange={() => setExpandedAccordion(expandedAccordion === 'keyLocations' ? false : 'keyLocations')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Key Locations ({details.keyLocations?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {(details.keyLocations || []).map((location: any, index: number) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Location Name"
                              value={location.name || ''}
                              onChange={(e) => updateTableRow('keyLocations', index, 'name', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Type"
                              value={location.type || ''}
                              onChange={(e) => updateTableRow('keyLocations', index, 'type', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Specialization/Advantage"
                              value={location.specialization || ''}
                              onChange={(e) => updateTableRow('keyLocations', index, 'specialization', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={10}>
                            <TextField
                              fullWidth
                              label="Link (URL)"
                              value={location.link || ''}
                              onChange={(e) => updateTableRow('keyLocations', index, 'link', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Button
                              fullWidth
                              color="error"
                              variant="outlined"
                              onClick={() => deleteTableRow('keyLocations', index)}
                              startIcon={<DeleteIcon />}
                            >
                              Delete
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addTableRow('keyLocations', { name: '', type: '', specialization: '', link: '' })}
                  >
                    Add Location
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Support Assets Cards */}
            <Accordion expanded={expandedAccordion === 'supportAssets'} onChange={() => setExpandedAccordion(expandedAccordion === 'supportAssets' ? false : 'supportAssets')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Support Assets ({details.supportAssets?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {(details.supportAssets || []).map((asset: any, index: number) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <TextField
                          fullWidth
                          label="Asset Name"
                          value={asset.name || ''}
                          onChange={(e) => updateCardName('supportAssets', index, e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        <Typography variant="subtitle2" gutterBottom>Bullet Points:</Typography>
                        {(asset.bulletPoints || []).map((point: string, bulletIndex: number) => (
                          <Box key={bulletIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              value={point}
                              onChange={(e) => updateBulletPoint('supportAssets', index, bulletIndex, e.target.value)}
                              placeholder={`Point ${bulletIndex + 1}`}
                            />
                            <IconButton
                              size="small"
                              onClick={() => deleteBulletPoint('supportAssets', index, bulletIndex)}
                              disabled={(asset.bulletPoints?.length || 0) === 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addBulletPoint('supportAssets', index)}
                          >
                            Add Bullet Point
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => deleteCardItem('supportAssets', index)}
                          >
                            Delete Asset
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addCardItem('supportAssets')}
                  >
                    Add Support Asset
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        );

      case 'events_networks':
        return (
          <>
            {/* Top Events Table */}
            <Accordion expanded={expandedAccordion === 'topEvents'} onChange={() => setExpandedAccordion(expandedAccordion === 'topEvents' ? false : 'topEvents')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Top Industry Events ({details.topEvents?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {(details.topEvents || []).map((event: any, index: number) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Event Name"
                              value={event.event || ''}
                              onChange={(e) => updateTableRow('topEvents', index, 'event', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Date"
                              value={event.date || ''}
                              onChange={(e) => updateTableRow('topEvents', index, 'date', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="City"
                              value={event.city || ''}
                              onChange={(e) => updateTableRow('topEvents', index, 'city', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Focus"
                              value={event.focus || ''}
                              onChange={(e) => updateTableRow('topEvents', index, 'focus', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={4}>
                            <TextField
                              fullWidth
                              label="Link (URL)"
                              value={event.link || ''}
                              onChange={(e) => updateTableRow('topEvents', index, 'link', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <Button
                              color="error"
                              variant="outlined"
                              onClick={() => deleteTableRow('topEvents', index)}
                              startIcon={<DeleteIcon />}
                            >
                              Delete Event
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addTableRow('topEvents', { event: '', date: '', city: '', focus: '', link: '' })}
                  >
                    Add Event
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Associations & Networks Cards */}
            <Accordion expanded={expandedAccordion === 'associations'} onChange={() => setExpandedAccordion(expandedAccordion === 'associations' ? false : 'associations')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Associations & Networks ({details.associations?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {(details.associations || []).map((association: any, index: number) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <TextField
                          fullWidth
                          label="Association Name"
                          value={association.name || ''}
                          onChange={(e) => updateCardName('associations', index, e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        <Typography variant="subtitle2" gutterBottom>Bullet Points:</Typography>
                        {(association.bulletPoints || []).map((point: string, bulletIndex: number) => (
                          <Box key={bulletIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              value={point}
                              onChange={(e) => updateBulletPoint('associations', index, bulletIndex, e.target.value)}
                              placeholder={`Point ${bulletIndex + 1}`}
                            />
                            <IconButton
                              size="small"
                              onClick={() => deleteBulletPoint('associations', index, bulletIndex)}
                              disabled={(association.bulletPoints?.length || 0) === 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addBulletPoint('associations', index)}
                          >
                            Add Bullet Point
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => deleteCardItem('associations', index)}
                          >
                            Delete Association
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addCardItem('associations')}
                  >
                    Add Association
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        );

      case 'funding_incentives':
        return (
          <>
            {/* Funding Programs Table */}
            <Accordion expanded={expandedAccordion === 'fundingPrograms'} onChange={() => setExpandedAccordion(expandedAccordion === 'fundingPrograms' ? false : 'fundingPrograms')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Funding Programs ({details.fundingPrograms?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {(details.fundingPrograms || []).map((program: any, index: number) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Program Name"
                              value={program.name || ''}
                              onChange={(e) => updateTableRow('fundingPrograms', index, 'name', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Stakeholder"
                              value={program.stakeholder || ''}
                              onChange={(e) => updateTableRow('fundingPrograms', index, 'stakeholder', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Specialization/Advantage"
                              value={program.specialization || ''}
                              onChange={(e) => updateTableRow('fundingPrograms', index, 'specialization', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={10}>
                            <TextField
                              fullWidth
                              label="Link (URL)"
                              value={program.link || ''}
                              onChange={(e) => updateTableRow('fundingPrograms', index, 'link', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Button
                              fullWidth
                              color="error"
                              variant="outlined"
                              onClick={() => deleteTableRow('fundingPrograms', index)}
                              startIcon={<DeleteIcon />}
                            >
                              Delete
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addTableRow('fundingPrograms', { name: '', specialization: '', stakeholder: '', link: '' })}
                  >
                    Add Funding Program
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Incentives & Grants Table */}
            <Accordion expanded={expandedAccordion === 'incentives'} onChange={() => setExpandedAccordion(expandedAccordion === 'incentives' ? false : 'incentives')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Incentives & Grants ({details.incentives?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {(details.incentives || []).map((incentive: any, index: number) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Program Name"
                              value={incentive.name || ''}
                              onChange={(e) => updateTableRow('incentives', index, 'name', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Stakeholder"
                              value={incentive.stakeholder || ''}
                              onChange={(e) => updateTableRow('incentives', index, 'stakeholder', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Specialization/Advantage"
                              value={incentive.specialization || ''}
                              onChange={(e) => updateTableRow('incentives', index, 'specialization', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={10}>
                            <TextField
                              fullWidth
                              label="Link (URL)"
                              value={incentive.link || ''}
                              onChange={(e) => updateTableRow('incentives', index, 'link', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Button
                              fullWidth
                              color="error"
                              variant="outlined"
                              onClick={() => deleteTableRow('incentives', index)}
                              startIcon={<DeleteIcon />}
                            >
                              Delete
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addTableRow('incentives', { name: '', specialization: '', stakeholder: '', link: '' })}
                  >
                    Add Incentive/Grant
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Investor Tools Cards */}
            <Accordion expanded={expandedAccordion === 'investorTools'} onChange={() => setExpandedAccordion(expandedAccordion === 'investorTools' ? false : 'investorTools')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Investor Tools ({details.investorTools?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {(details.investorTools || []).map((tool: any, index: number) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <TextField
                          fullWidth
                          label="Tool Name"
                          value={tool.name || ''}
                          onChange={(e) => updateCardName('investorTools', index, e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        <Typography variant="subtitle2" gutterBottom>Bullet Points:</Typography>
                        {(tool.bulletPoints || []).map((point: string, bulletIndex: number) => (
                          <Box key={bulletIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              value={point}
                              onChange={(e) => updateBulletPoint('investorTools', index, bulletIndex, e.target.value)}
                              placeholder={`Point ${bulletIndex + 1}`}
                            />
                            <IconButton
                              size="small"
                              onClick={() => deleteBulletPoint('investorTools', index, bulletIndex)}
                              disabled={(tool.bulletPoints?.length || 0) === 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addBulletPoint('investorTools', index)}
                          >
                            Add Bullet Point
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => deleteCardItem('investorTools', index)}
                          >
                            Delete Tool
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addCardItem('investorTools')}
                  >
                    Add Investor Tool
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        );

      case 'talent_innovation':
        return (
          <>
            {/* Universities Table */}
            <Accordion expanded={expandedAccordion === 'universities'} onChange={() => setExpandedAccordion(expandedAccordion === 'universities' ? false : 'universities')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Universities, Research Centers, and Academies ({details.universities?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {(details.universities || []).map((university: any, index: number) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Organization Name"
                              value={university.name || ''}
                              onChange={(e) => updateTableRow('universities', index, 'name', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Type"
                              value={university.type || ''}
                              onChange={(e) => updateTableRow('universities', index, 'type', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Programs"
                              value={university.programs || ''}
                              onChange={(e) => updateTableRow('universities', index, 'programs', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={10}>
                            <TextField
                              fullWidth
                              label="Link (URL)"
                              value={university.link || ''}
                              onChange={(e) => updateTableRow('universities', index, 'link', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Button
                              fullWidth
                              color="error"
                              variant="outlined"
                              onClick={() => deleteTableRow('universities', index)}
                              startIcon={<DeleteIcon />}
                            >
                              Delete
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addTableRow('universities', { name: '', type: '', programs: '', link: '' })}
                  >
                    Add University/Research Center
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Innovation Hubs Table */}
            <Accordion expanded={expandedAccordion === 'innovationHubs'} onChange={() => setExpandedAccordion(expandedAccordion === 'innovationHubs' ? false : 'innovationHubs')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Innovation Hubs, Accelerators, and Incubators ({details.innovationHubs?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {(details.innovationHubs || []).map((hub: any, index: number) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Organization Name"
                              value={hub.name || ''}
                              onChange={(e) => updateTableRow('innovationHubs', index, 'name', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <TextField
                              fullWidth
                              label="Type"
                              value={hub.type || ''}
                              onChange={(e) => updateTableRow('innovationHubs', index, 'type', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="Programs"
                              value={hub.programs || ''}
                              onChange={(e) => updateTableRow('innovationHubs', index, 'programs', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={10}>
                            <TextField
                              fullWidth
                              label="Link (URL)"
                              value={hub.link || ''}
                              onChange={(e) => updateTableRow('innovationHubs', index, 'link', e.target.value)}
                              size="small"
                            />
                          </Grid>
                          <Grid item xs={12} md={2}>
                            <Button
                              fullWidth
                              color="error"
                              variant="outlined"
                              onClick={() => deleteTableRow('innovationHubs', index)}
                              startIcon={<DeleteIcon />}
                            >
                              Delete
                            </Button>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addTableRow('innovationHubs', { name: '', type: '', programs: '', link: '' })}
                  >
                    Add Innovation Hub/Accelerator
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>

            {/* Skills Localization Cards */}
            <Accordion expanded={expandedAccordion === 'skillsLocalization'} onChange={() => setExpandedAccordion(expandedAccordion === 'skillsLocalization' ? false : 'skillsLocalization')}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1" fontWeight={600}>
                  Skills Localization and Future Talent Pipeline ({details.skillsLocalization?.length || 0})
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Stack spacing={2}>
                  {(details.skillsLocalization || []).map((skill: any, index: number) => (
                    <Card key={index} variant="outlined">
                      <CardContent>
                        <TextField
                          fullWidth
                          label="Program Name"
                          value={skill.name || ''}
                          onChange={(e) => updateCardName('skillsLocalization', index, e.target.value)}
                          sx={{ mb: 2 }}
                        />
                        <Typography variant="subtitle2" gutterBottom>Bullet Points:</Typography>
                        {(skill.bulletPoints || []).map((point: string, bulletIndex: number) => (
                          <Box key={bulletIndex} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              value={point}
                              onChange={(e) => updateBulletPoint('skillsLocalization', index, bulletIndex, e.target.value)}
                              placeholder={`Point ${bulletIndex + 1}`}
                            />
                            <IconButton
                              size="small"
                              onClick={() => deleteBulletPoint('skillsLocalization', index, bulletIndex)}
                              disabled={(skill.bulletPoints?.length || 0) === 1}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                        ))}
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() => addBulletPoint('skillsLocalization', index)}
                          >
                            Add Bullet Point
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => deleteCardItem('skillsLocalization', index)}
                          >
                            Delete Program
                          </Button>
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={() => addCardItem('skillsLocalization')}
                  >
                    Add Skills Program
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const currentColor = categories[tabValue].color;

  return (
    <Box>
      <Tabs
        value={tabValue}
        onChange={(_, newValue) => setTabValue(newValue)}
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        {categories.map((cat, index) => (
          <Tab
            key={cat.value}
            icon={cat.icon}
            iconPosition="start"
            label={cat.label}
            sx={{
              '&.Mui-selected': {
                color: cat.color.main
              }
            }}
          />
        ))}
      </Tabs>

      {categories.map((cat, index) => (
        <Box
          key={cat.value}
          role="tabpanel"
          hidden={tabValue !== index}
          sx={{ py: 2 }}
        >
          {tabValue === index && (
            <Box>
              {/* Header with Save button */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box>
                  <Typography variant="h6" fontWeight={600} sx={{ color: cat.color.dark }}>
                    {cat.label}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formData.id ? 'Edit content for this category' : 'Create content for this category'}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                  onClick={handleSave}
                  disabled={!formData.name || !formData.leadText || saving}
                  sx={{
                    background: `linear-gradient(135deg, ${cat.color.main}, ${cat.color.dark})`,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${cat.color.dark}, ${cat.color.main})`
                    }
                  }}
                >
                  {saving ? 'Saving...' : (formData.id ? 'Save Changes' : 'Create Page')}
                </Button>
              </Box>

              {/* Form Content */}
              <Stack spacing={3}>
                {/* Basic Information */}
                <Accordion expanded={expandedAccordion === 'basic'} onChange={() => setExpandedAccordion(expandedAccordion === 'basic' ? false : 'basic')}>
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    sx={{
                      bgcolor: alpha(cat.color.main, 0.05),
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="subtitle1" fontWeight={600}>
                      Basic Information *
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Page Title"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        required
                        helperText="This appears at the top of the detail page"
                      />
                      <TextField
                        fullWidth
                        label="Lead Text (Summary)"
                        value={formData.leadText}
                        onChange={(e) => setFormData({ ...formData, leadText: e.target.value })}
                        multiline
                        rows={2}
                        required
                        helperText="Brief description shown in the card on the overview page"
                      />
                      <TextField
                        fullWidth
                        label="Introduction Paragraph"
                        value={formData.intro}
                        onChange={(e) => setFormData({ ...formData, intro: e.target.value })}
                        multiline
                        rows={4}
                        helperText="Detailed introduction shown below the header on detail page"
                      />
                    </Stack>
                  </AccordionDetails>
                </Accordion>

                {/* Category-specific content */}
                {renderCategoryForm()}
              </Stack>
            </Box>
          )}
        </Box>
      ))}
    </Box>
  );
};

export default UserFriendlySupportEcosystemEditor;
